import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { DateTime } from 'luxon';
import { ApolloCache, FetchResult } from '@apollo/client';
import {
  SortOrder,
  SplitTimeSignalInput,
  SplitTimeSingalMutation,
  TimeSignal,
  TimeSignalAttribute,
  TimeSignalClassification,
  TimeSignalsDocument,
  TimeSignalsQuery,
  useSplitTimeSingalMutation
} from 'graphql/types';
import { mapDateToISOString } from 'utils/date-time';
import {
  ALL_CLASSIFICATIONS,
  TimeSignalGetParam,
  useTimeSignalGetParam
} from 'modules/notes/hooks/useTimeSignalParam';
import {
  NotesClassification,
  NOTES_PAGE_TYPE,
  useNotesFilterContext
} from 'modules/notes/NotesFilterContextProvider';
import { getTime } from 'modules/notes/addNote/hooks';
import { useMeContext } from 'modules/me';
import { TIMELINE_TIMESIGNAL_COUNT } from 'modules/notes/Timeline/hooks';
import {
  FormTimesheetLabel,
  CustomLabel
} from 'modules/timesheet-labels/labelSchema';
import { SearchCriteria } from 'modules/notes/common';
import { useNotesToastContext } from 'modules/notes/NotesToastContextProvider';
import { useIsSmartSuggestionSettingEnabled } from 'modules/timesheet-labels/hooks/useIsSmartSuggestionSettingEnabled';
import {
  isSearchCriteriaMatch,
  shouldAddToCache,
  sortedTimeSignals
} from 'modules/notes/cache/utils/cache-utils';
import { useNotesSortContext } from 'modules/notes/NotesSortContextProvider';
import { useNotesAnimationContext } from 'modules/notes/providers';
import { NotesActions } from 'modules/notes/notesSelector/hooks';
import { clearCachedDates } from 'modules/notes/cache/utils/time-signal-utils';
import { OFFSET_ENTRY } from '../types';
import { mapTimesheetLabel, truncateMessage } from '../../common/utils';
import { SplitNoteFormData } from '../splitNoteSchema';

type ReturnType = { saveSplitNote: () => void; loading: boolean };

export const canAddNoteToCache = (
  param: TimeSignalGetParam,
  currentClassification: NotesClassification,
  timeSignal: TimeSignal,
  searchCriteria: SearchCriteria
): boolean => {
  if (
    currentClassification === 'All' &&
    !ALL_CLASSIFICATIONS.includes(timeSignal.classification)
  ) {
    return false;
  } else if (
    currentClassification !== 'All' &&
    TimeSignalClassification[currentClassification] !==
      timeSignal.classification
  ) {
    return false;
  }

  if (
    timeSignal.date < param.input.filter?.fromDate ||
    timeSignal.date > param.input.filter?.toDate
  ) {
    return false;
  }
  if (!isSearchCriteriaMatch(timeSignal, searchCriteria)) {
    return false;
  }
  const searchText =
    timeSignal.labelValues.reduce((acc, label) => {
      return label.value?.stringValue ? acc + label.value.stringValue : acc;
    }, '') + timeSignal.description;

  const keywords =
    param.input.filter?.searchText?.toLowerCase().split(/\s+/) ?? [];
  return keywords.length === 0
    ? true
    : keywords.every((key) => searchText.toLowerCase().includes(key));
};

export const appendTimeSignals = (
  filteredSignals: TimeSignal[],
  entriesToAdd: TimeSignal[],
  entriesCanAdd: TimeSignal[],
  sortOrder: SortOrder
): TimeSignal[] => {
  if (entriesToAdd.length > 0) {
    const date = DateTime.fromISO(entriesToAdd[0].date);
    const beforeItems = filteredSignals.filter(
      (s) => DateTime.fromISO(s.date) > date
    );
    const items = filteredSignals.filter((s) =>
      DateTime.fromISO(s.date).equals(date)
    );
    const afterItems = filteredSignals.filter(
      (s) => DateTime.fromISO(s.date) < date
    );

    const entries = entriesToAdd.reduce(
      (acc: TimeSignal[], entry: TimeSignal) => {
        acc = sortedTimeSignals(acc, sortOrder, entry);

        return acc;
      },
      items
    );

    return [...beforeItems, ...entries, ...afterItems];
  } else {
    return entriesCanAdd.reduce((entries, entry) => {
      const date = DateTime.fromISO(entry.date);
      const beforeItems = entries.filter(
        (s) => DateTime.fromISO(s.date) > date
      );
      const afterItems = entries.filter(
        (s) => DateTime.fromISO(s.date) <= date
      );
      return [...beforeItems, entry, ...afterItems];
    }, filteredSignals);
  }
};

export const updateCreateCache = ({
  param,
  currentClassification,
  originalNoteId,
  searchCriteria,
  sortOrder
}: {
  param: TimeSignalGetParam;
  currentClassification: NotesClassification;
  originalNoteId: string;
  searchCriteria: SearchCriteria;
  sortOrder: SortOrder;
}) => (
  cache: ApolloCache<SplitTimeSingalMutation>,
  mutationResult: FetchResult<
    SplitTimeSingalMutation,
    Record<string, any>,
    Record<string, any>
  >
): void => {
  if (mutationResult?.data?.splitTimeSignal) {
    const existingSignals: TimeSignalsQuery | null = cache.readQuery({
      query: TimeSignalsDocument,
      variables: param
    });
    if (existingSignals) {
      const entriesCanAdd = mutationResult.data.splitTimeSignal.timeSignals.filter(
        (signal) =>
          canAddNoteToCache(
            param,
            currentClassification,
            signal,
            searchCriteria
          )
      );
      const entriesToAdd = entriesCanAdd.filter((signal) =>
        shouldAddToCache(existingSignals, signal, sortOrder)
      );
      const filteredSignals = existingSignals.timeSignals.items.filter(
        (entry) => entry.id !== originalNoteId
      );
      const orderedSignals = appendTimeSignals(
        filteredSignals,
        entriesToAdd,
        entriesCanAdd,
        sortOrder
      );

      cache.writeQuery({
        query: TimeSignalsDocument,
        variables: param,
        data: {
          timeSignals: entriesToAdd.length
            ? {
                __typename: 'TimeSignalsResult',
                items: orderedSignals,
                cursor: existingSignals.timeSignals.cursor,
                totalCount:
                  existingSignals.timeSignals.totalCount +
                  entriesCanAdd.length -
                  1
              }
            : {
                __typename: 'TimeSignalsResult',
                items: filteredSignals,
                cursor: existingSignals.timeSignals.cursor,
                totalCount: existingSignals.timeSignals.totalCount - 1
              }
        }
      });
    }
  }
};

const getMessageId = (
  selectedFolder: NotesClassification,
  targetClassification: TimeSignalClassification
) => {
  return selectedFolder === 'All' ||
    selectedFolder.toLocaleLowerCase() ===
      targetClassification.toLocaleLowerCase()
    ? 'toast.note.split.successfulSplitTo.common'
    : `toast.note.split.successfulSplitTo.${targetClassification.toLocaleLowerCase()}`;
};

export const useSaveSplitNote = ({
  note,
  attributes,
  onComplete
}: {
  note: TimeSignal;
  onComplete: () => void;
  attributes?: TimeSignalAttribute[];
}): ReturnType => {
  const {
    filterContext: { selectedClassification, pageType, searchCriteria }
  } = useNotesFilterContext();
  const { id, locale } = useMeContext() || {};
  const ianaName = locale?.timeZone?.ianaName;
  const [splitTimeSignal, { loading }] = useSplitTimeSingalMutation();
  const param = useTimeSignalGetParam(
    pageType === NOTES_PAGE_TYPE.TIMELINE
      ? TIMELINE_TIMESIGNAL_COUNT
      : undefined
  );
  const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();
  const { setToastContext } = useNotesToastContext();
  const { sortOrder } = useNotesSortContext();
  const { setNotesToAnimate } = useNotesAnimationContext();

  const { handleSubmit } = useFormContext();
  const splitNote = useCallback(
    async (data: SplitNoteFormData) => {
      const input: SplitTimeSignalInput = {
        id: note.id,
        timeSignals: data.splitEntries
          .filter((splitEntry) => splitEntry.id !== OFFSET_ENTRY)
          .map((splitEntry) => {
            let startTime = splitEntry.startTime;
            let endTime = splitEntry.endTime;
            const noteDate = DateTime.fromISO(note.date).toJSDate();
            const labels = [
              ...(splitEntry.timesheetLabels || [])
                .filter(
                  ({ label }: FormTimesheetLabel) =>
                    label.value ||
                    (isSmartSuggestionEnabled && label.suggestedValues)
                )
                .map(({ label }) =>
                  mapTimesheetLabel(isSmartSuggestionEnabled, label)
                ),
              ...(splitEntry.customLabels || [])
                .filter(
                  (l: CustomLabel) =>
                    l.value || (isSmartSuggestionEnabled && l.suggestedValues)
                )
                .map((l) => mapTimesheetLabel(isSmartSuggestionEnabled, l))
            ];
            if (splitEntry.date && startTime) {
              startTime = getTime(noteDate, startTime, ianaName);
            }
            if (splitEntry.date && endTime) {
              endTime = getTime(noteDate, endTime, ianaName);
            }
            return {
              date: note.date,
              sources: note.sources.map((source) => ({
                id: source.id,
                name: source.name,
                ref: source.name,
                image: source.image ? { src: source.image?.src } : null
              })),
              classification: data.classification,
              description: splitEntry.description,
              startTime: mapDateToISOString(startTime),
              endTime: mapDateToISOString(endTime),
              duration: Math.round(splitEntry.duration ?? 0),
              attributes: (attributes ?? []).map((attribute) => ({
                name: attribute.name,
                value: attribute.value,
                sourceId: attribute.sourceId,
                sourceRefs: attribute.sourceRefs,
                visible: attribute.visible
              })),
              labelValues: labels,
              labelValuesInferenceId: splitEntry.labelValuesInferenceId
            };
          })
      };

      try {
        clearCachedDates(id);

        const result = await splitTimeSignal({
          variables: { input },
          update: updateCreateCache({
            param,
            currentClassification: selectedClassification,
            originalNoteId: note.id,
            searchCriteria,
            sortOrder
          }),
          awaitRefetchQueries: true,
          refetchQueries: ['timeSignalsCount', 'timeSignalsTotals']
        });
        if (result.data?.splitTimeSignal.timeSignals.length) {
          setNotesToAnimate(
            result.data.splitTimeSignal.timeSignals.map((signal) => ({
              id: signal.id,
              action: 'SplitNote',
              folder: data.classification.toString() as NotesActions
            }))
          );
          setTimeout(() => setNotesToAnimate([]), 2000);
        }
        setToastContext({
          showSuccess: true,
          message: getMessageId(selectedClassification, data.classification),
          values: {
            message: truncateMessage(note.description ?? ''),
            count: `${data.splitEntries.length}`
          }
        });
      } catch (e) {
        setToastContext({
          showError: true,
          message: 'toast.note.split.failure'
        });
        return;
      }

      onComplete();
    },
    [
      note.id,
      note.date,
      note.sources,
      note.description,
      onComplete,
      attributes,
      isSmartSuggestionEnabled,
      ianaName,
      splitTimeSignal,
      param,
      selectedClassification,
      searchCriteria,
      sortOrder,
      setToastContext,
      id,
      setNotesToAnimate
    ]
  );

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return { saveSplitNote: handleSubmit(splitNote), loading };
};
